import { apiHandler } from "../buyerApiHandler";

/**
 * Card Studio: how restaurant cards look on the buyer home.
 * Buyer API /admin/restaurant-cards (x-api-key, sent by apiHandler).
 *
 * The types and ranges below mirror the Buyer API contract in
 * ONDC-Buyer-API/src/restaurant-card/card-style.ts. The server sanitises
 * everything (clamps numbers, drops bad colours / URLs), so keep RANGES in
 * step with sanitizeCardStyle() / sanitizeCardOverride().
 */

export const CARD_LAYOUTS = ["overlay", "split", "classic"] as const;
export const NAME_POSITIONS = ["top-left", "center-left", "bottom-left"] as const;
export const BADGE_POSITIONS = ["top-left", "top-right", "hidden"] as const;
export const TIME_POSITIONS = ["bottom-right", "top-right", "hidden"] as const;
export const TAGLINE_FONTS = ["script", "bold", "serif"] as const;
export const BADGE_MODES = ["auto", "custom", "none"] as const;

export type CardLayout = (typeof CARD_LAYOUTS)[number];
export type NamePosition = (typeof NAME_POSITIONS)[number];
export type BadgePosition = (typeof BADGE_POSITIONS)[number];
export type TimePosition = (typeof TIME_POSITIONS)[number];
export type TaglineFont = (typeof TAGLINE_FONTS)[number];
export type BadgeMode = (typeof BADGE_MODES)[number];

export interface CardPositions {
  name: NamePosition;
  badge: BadgePosition;
  time: TimePosition;
  chips: boolean;
}

export interface CardColors {
  name: string;
  meta: string;
  ratingBg: string;
  chipBg: string;
  chipText: string;
  timeBg: string;
  timeText: string;
}

export interface CardStyle {
  layout: CardLayout;
  overlay: "dark" | "light";
  overlayStrength: number;
  height: number;
  radius: number;
  borderColor: string | null;
  borderWidth: number;
  shadow: boolean;
  positions: CardPositions;
  colors: CardColors;
  nameWeight: number;
  photos: { rolling: boolean; intervalSec: number };
}

export interface CardBadge {
  mode: BadgeMode;
  text: string;
  color: string;
  icon: string;
}

export interface CardTagline {
  text: string;
  font: TaglineFont;
  color: string;
  x: number;
  y: number;
  size: number;
  rotate: number;
}

export interface CardChip {
  key: string;
  label: string;
  icon: string;
}

export interface CardLayoutTweaks {
  layout?: CardLayout;
  overlay?: "dark" | "light";
  overlayStrength?: number;
  positions?: Partial<CardPositions>;
}

export interface CardOverride {
  enabled: boolean;
  cover_image_url: string | null;
  gallery: string[];
  badge: CardBadge | null;
  tagline: CardTagline | null;
  chips: CardChip[];
  layout: CardLayoutTweaks;
  pin_rank: number | null;
}

export type ChipPresets = Record<string, { label: string; icon: string }>;

export const DEFAULT_CARD_STYLE: CardStyle = {
  layout: "overlay",
  overlay: "dark",
  overlayStrength: 0.75,
  height: 196,
  radius: 22,
  borderColor: null,
  borderWidth: 0,
  shadow: true,
  positions: { name: "center-left", badge: "top-left", time: "bottom-right", chips: true },
  colors: {
    name: "#FFFFFF",
    meta: "rgba(255,255,255,0.88)",
    ratingBg: "#1BA94C",
    chipBg: "rgba(0,0,0,0.45)",
    chipText: "#FFFFFF",
    timeBg: "#FFFFFF",
    timeText: "#1A1A1A",
  },
  nameWeight: 800,
  photos: { rolling: true, intervalSec: 3 },
};

/** Colours the server falls back to when the overlay is "light". */
export const LIGHT_CARD_COLORS: CardColors = {
  name: "#1A1A1A",
  meta: "#4A4A4A",
  ratingBg: "#1BA94C",
  chipBg: "#FFFFFF",
  chipText: "#2A2A2A",
  timeBg: "#FFFFFF",
  timeText: "#1A1A1A",
};

/** Fallback chip presets if the server sends none (same as CHIP_PRESETS). */
export const DEFAULT_CHIP_PRESETS: ChipPresets = {
  hygienic: { label: "Hygienic", icon: "verified-user" },
  "great-taste": { label: "Great Taste", icon: "thumb-up" },
  budget: { label: "Budget Friendly", icon: "currency-rupee" },
  fresh: { label: "Fresh Ingredients", icon: "eco" },
  "quick-bites": { label: "Quick Bites", icon: "bolt" },
  value: { label: "Value for Money", icon: "savings" },
  "quick-delivery": { label: "Quick Delivery", icon: "delivery-dining" },
  "pure-veg": { label: "Pure Veg", icon: "spa" },
  "family-pack": { label: "Family Packs", icon: "groups" },
  "late-night": { label: "Late Night", icon: "nightlight-round" },
};

/** Slider ranges, identical to the server's clamps. */
export const CARD_RANGES = {
  overlayStrength: { min: 0, max: 1, step: 0.05 },
  height: { min: 140, max: 280, step: 2 },
  radius: { min: 0, max: 36, step: 1 },
  borderWidth: { min: 0, max: 4, step: 0.5 },
  nameWeight: { min: 500, max: 900, step: 100 },
  intervalSec: { min: 2, max: 10, step: 0.5 },
  taglineSize: { min: 0.6, max: 1.8, step: 0.05 },
  taglineRotate: { min: -30, max: 30, step: 1 },
  pinRank: { min: 1, max: 100 },
} as const;

export const CARD_LIMITS = {
  badgeText: 24,
  taglineText: 40,
  chipLabel: 24,
  chips: 3,
  gallery: 4,
  photoMb: 3,
};

export const PHOTO_ACCEPT = ".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp";

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGBA = /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+)\s*)?\)$/i;
export const isCardColor = (v: unknown): v is string =>
  typeof v === "string" && (HEX.test(v.trim()) || RGBA.test(v.trim()));
export const isHttpUrl = (v: unknown): v is string =>
  typeof v === "string" && /^https?:\/\/\S{3,2000}$/i.test(v.trim());

/** Fill gaps so the editor and preview always have every field. */
export const styleWithDefaults = (raw: any): CardStyle => {
  const r = raw && typeof raw === "object" ? raw : {};
  const d = DEFAULT_CARD_STYLE;
  const base = r.overlay === "light" ? LIGHT_CARD_COLORS : d.colors;
  return {
    ...d,
    ...r,
    borderColor: r.borderColor ?? null,
    positions: { ...d.positions, ...(r.positions ?? {}) },
    colors: { ...base, ...(r.colors ?? {}) },
    photos: { ...d.photos, ...(r.photos ?? {}) },
  };
};

export const emptyOverride = (): CardOverride => ({
  enabled: true,
  cover_image_url: null,
  gallery: [],
  badge: null,
  tagline: null,
  chips: [],
  layout: {},
  pin_rank: null,
});

export const overrideWithDefaults = (raw: any): CardOverride => {
  const r = raw && typeof raw === "object" ? raw : {};
  return {
    ...emptyOverride(),
    ...r,
    gallery: Array.isArray(r.gallery) ? r.gallery : [],
    chips: Array.isArray(r.chips) ? r.chips : [],
    layout: r.layout && typeof r.layout === "object" ? r.layout : {},
  };
};

/** Global style with one restaurant's layout tweaks on top (same as the app). */
export const mergeStyle = (style: CardStyle, tweaks?: CardLayoutTweaks | null): CardStyle => {
  if (!tweaks) return style;
  const overlay = tweaks.overlay ?? style.overlay;
  // Flipping the overlay on one card swaps to that overlay's default colours,
  // otherwise dark text would land on a dark fade (and vice versa).
  const colors =
    overlay !== style.overlay ? (overlay === "light" ? LIGHT_CARD_COLORS : DEFAULT_CARD_STYLE.colors) : style.colors;
  return {
    ...style,
    layout: tweaks.layout ?? style.layout,
    overlay,
    overlayStrength: tweaks.overlayStrength ?? style.overlayStrength,
    positions: { ...style.positions, ...(tweaks.positions ?? {}) },
    colors,
  };
};

export type CardStoreRow = {
  id: number;
  name: string;
  logo_url: string | null;
  food_type: string | null;
  status: string | null;
  customised: boolean;
  enabled: boolean;
  pin_rank: number | null;
  cover_image_url: string | null;
};

export type CardStoreList = { total: number; page: number; limit: number; stores: CardStoreRow[] };

export type CardStoreDetail = {
  store: { id: number; name: string; logo_url: string | null; food_type: string | null };
  override: CardOverride | null;
};

const json = { headers: { "Content-Type": "application/json" } };
const multipart = { headers: { "Content-Type": "multipart/form-data" } };

// The Buyer API wraps bodies in { success, data }.
const unwrap = (res: any) => res?.data ?? res;

const normaliseDetail = (body: any): CardStoreDetail => ({
  store: body?.store ?? { id: 0, name: "", logo_url: null, food_type: null },
  override: body?.override ? overrideWithDefaults(body.override) : null,
});

export const getCardStyle = async (): Promise<{ style: CardStyle; chip_presets: ChipPresets }> => {
  const body = unwrap(await apiHandler("get", "/admin/restaurant-cards/style"));
  const presets = body?.chip_presets && typeof body.chip_presets === "object" ? body.chip_presets : {};
  return {
    style: styleWithDefaults(body?.style),
    chip_presets: Object.keys(presets).length ? presets : DEFAULT_CHIP_PRESETS,
  };
};

export const saveCardStyle = async (style: CardStyle): Promise<CardStyle> =>
  styleWithDefaults(unwrap(await apiHandler("patch", "/admin/restaurant-cards/style", style, json)));

export const listCardStores = async (params: {
  search?: string;
  page?: number;
  limit?: number;
}): Promise<CardStoreList> => {
  const body = unwrap(
    await apiHandler("get", "/admin/restaurant-cards", undefined, {
      params: {
        ...(params.search?.trim() ? { search: params.search.trim() } : {}),
        page: params.page ?? 1,
        limit: params.limit ?? 20,
      },
    }),
  );
  return {
    total: Number(body?.total) || 0,
    page: Number(body?.page) || 1,
    limit: Number(body?.limit) || params.limit || 20,
    stores: Array.isArray(body?.stores) ? body.stores : [],
  };
};

export const getCardStore = async (storeId: number): Promise<CardStoreDetail> =>
  normaliseDetail(unwrap(await apiHandler("get", `/admin/restaurant-cards/${storeId}`)));

export const saveCardStore = async (storeId: number, override: CardOverride): Promise<CardStoreDetail> =>
  normaliseDetail(unwrap(await apiHandler("put", `/admin/restaurant-cards/${storeId}`, override, json)));

export const removeCardStore = (storeId: number) =>
  apiHandler("delete", `/admin/restaurant-cards/${storeId}`);

export const uploadCardPhoto = async (storeId: number, file: File): Promise<string> => {
  const fd = new FormData();
  fd.append("image", file);
  const body = unwrap(await apiHandler("post", `/admin/restaurant-cards/${storeId}/photo`, fd, multipart));
  const url = body?.url;
  if (!isHttpUrl(url)) throw new Error("Upload did not return a photo link");
  return url;
};
